import { useRouterState } from '@tanstack/react-router';
import { useSyncExternalStore } from 'react';

/**
 * 跨頁的一句提示（「密碼已重設，請重新登入」）走 **history state**，不走網址：
 * 重新整理或分享連結時它不會再出現一次，而網址列上也不會多一個 `?notice=`。
 */
export type AuthFlash = 'password_reset';

declare module '@tanstack/history' {
  interface HistoryState {
    authFlash?: AuthFlash;
  }
}

const FLASH_TEXT: Record<AuthFlash, string> = {
  password_reset: '密碼已重設，請重新登入。',
};

export function useAuthFlash(): string | undefined {
  const flash = useRouterState({ select: (state) => state.location.state.authFlash });
  return flash === undefined ? undefined : FLASH_TEXT[flash];
}

/**
 * 根版面最上方的一句提示（`SiteFlash` 元件）：給「離開之後落在任何一頁」的事件，例如刪除帳號。
 *
 * 它**不**走 history state：落點頁自己可能再 redirect 一次（儀表板會補上 `?symbol=`），
 * 那一跳會把 state 換掉，提示就在使用者看到之前消失了。所以放在記憶體裡——重新整理一樣會消失，
 * 由 `SiteFlash` 在使用者關掉或換到別的路徑時清掉。
 */
export type SiteFlashKind = 'account_deleted';

export const SITE_FLASH_TEXT: Record<SiteFlashKind, string> = {
  account_deleted: '您的帳號與相關資料已刪除。感謝您使用 Stockable。',
};

let siteFlash: SiteFlashKind | null = null;
const listeners = new Set<() => void>();

export function setSiteFlash(next: SiteFlashKind | null): void {
  siteFlash = next;
  for (const listener of listeners) listener();
}

export function useSiteFlash(): SiteFlashKind | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => siteFlash,
    () => null,
  );
}
